import { Box } from "@mui/material";
import * as React from "react";
import { Base } from "ui/Scripts/source/framework/base";
import { removeConfirmationWord } from "ui/Scripts/source/models/consts";
import { ConfirmationDialogResult } from "ui/Scripts/source/models/enums";
import { Translations } from "ui/Scripts/source/models/translations";
import { AppUtils } from "../../../framework/appUtils";
import { ICompanyEdit } from "../../../models/company/companyEdit";
import { ICompanyEditItem } from "../../../models/company/companyEditItem";
import * as companyService from "../../../models/services/companyService";
import { ISessionState } from "../../../models/session/sessionState";
import { Dialog } from "../../framework/Dialog/Dialog";
import { DialogItemsNav } from "../../framework/Dialog/DialogItemsNav";
import { DialogTabs } from "../../framework/Dialog/DialogTabs";
import { CompanyEdit as CompanyEditView, CompanyEditViewMode } from "../CompanyEdit";
import { DialogButtons } from "source/components/framework/Dialog/DialogButtons";

export interface ICompanyEditDialogProp {
    sessionState: ISessionState;
    companyId: string;
    classes?: string;
    hasPrevious: boolean;
    hasNext: boolean;
    defaultMenuItem: CompanyEditViewMode;
    onOk: () => void;
    onCancel: (listRefreshNeeded: boolean) => void;
    onRefreshList: () => Promise<void>;
    onNewSaved: (companyId: string) => void;
    onPrevious: () => void;
    onNext: () => void;
    onRefreshSessionState: () => void;
}

export interface ICompanyEditDialogState {
    companyEditViewMode: CompanyEditViewMode;
    companyEdit: ICompanyEdit;
    company: ICompanyEditItem;
}

export class CompanyEditDialog extends React.Component<ICompanyEditDialogProp, ICompanyEditDialogState> {
    private companyEditViewRef = React.createRef<CompanyEditView>();

    constructor(props: ICompanyEditDialogProp) {
        super(props);
        this.state = {
            companyEditViewMode: Base.isNullOrUndefined(props.defaultMenuItem) ? CompanyEditViewMode.General : props.defaultMenuItem,
            companyEdit: null,
            company: null
        };
    }

    loadCompany = async () => {
        const props = this.props;
        const result = await AppUtils.callService(() => companyService.getCompanyEdit(props.companyId), false);
        if (!result) return;
        for (const applicationPermission of result.applicationPermissions) {
            result.unitModuleNode.setApplicationPermissionTypeVisible(applicationPermission.type, applicationPermission.level > 0);
        }
        this.setState({
            companyEdit: result,
            company: result.company
        });
    };

    componentDidMount(): void {
        this.loadCompany();
    }

    async componentDidUpdate(prevProps: ICompanyEditDialogProp, prevState: ICompanyEditDialogState): Promise<void> {
        const props = this.props;
        const prevDefaultMenuItem = Base.isNullOrUndefined(prevProps.defaultMenuItem) ? CompanyEditViewMode.General : prevProps.defaultMenuItem;
        const defaultMenuItem = Base.isNullOrUndefined(props.defaultMenuItem) ? CompanyEditViewMode.General : props.defaultMenuItem;
        if (prevDefaultMenuItem === defaultMenuItem) return;
        this.setState({
            companyEditViewMode: defaultMenuItem
        });
    }

    handleToggleActiveMenuItem = (companyEditViewMode: CompanyEditViewMode) => {
        this.setState({ companyEditViewMode });
    };

    handleCompanyLoaded = (company: ICompanyEditItem) => {
        this.setState({
            company
        });
    };

    getCompanyGroupsEnabled = (): boolean => {
        return this.companyEditViewRef && this.companyEditViewRef.current && this.companyEditViewRef.current.getCompanyGroupsEnabled();
    };

    getPartnerCompaniesEnabled = (): boolean => {
        return this.companyEditViewRef && this.companyEditViewRef.current && this.companyEditViewRef.current.getPartnerCompaniesEnabled();
    };

    getExternalSystemsExists = (): boolean => {
        return this.companyEditViewRef && this.companyEditViewRef.current && this.companyEditViewRef.current.getExternalSystemsExists();
    };
    
    getExternalIntraSystemsExists = (): boolean => {
        return this.companyEditViewRef && this.companyEditViewRef.current && this.companyEditViewRef.current.getExternalIntraSystemsExists();
    };
    
    getCompanyDataWasModified = (): boolean => {
        return this.companyEditViewRef && this.companyEditViewRef.current && this.companyEditViewRef.current.getCompanyDataWasModified();
    };

    handleSave = () => {
        if (this.companyEditViewRef && this.companyEditViewRef.current) {
            this.companyEditViewRef.current.save();
        }
    };

    handleRemove = async () => {
        const props = this.props;
        const state = this.state;
        if (!state.company || state.company.isNew()) return;
        if (await AppUtils.showConfirmationDialog(String.format(Translations.DoYouReallyWantToRemoveCompanyParameter, state.company.code), false, removeConfirmationWord) === ConfirmationDialogResult.Yes) {
            // Call server
            const result = await AppUtils.callService(() => companyService.removeCompany(props.companyId));
            if (!result) return;
            props.onCancel(true);
        }
    };

    handleCancel = async () => {
        const props = this.props;
        if (this.companyEditViewRef && this.companyEditViewRef.current) {
            const cancelResult = await this.companyEditViewRef.current.cancel(false);
            if (Base.isNullOrUndefined(cancelResult)) return;
            if (cancelResult === ConfirmationDialogResult.Cancel) return;
            if (cancelResult) {
                props.onOk();
            } else {
                props.onCancel(this.getCompanyDataWasModified());
            }
        }
    };

    canMoveOut = async (): Promise<boolean> => {
        const props = this.props;
        if (!this.companyEditViewRef || !this.companyEditViewRef.current) return false;
        const cancelResult = await this.companyEditViewRef.current.cancel(false);
        if (Base.isNullOrUndefined(cancelResult)) return false;
        if (cancelResult) {
            await props.onRefreshList();
        }
        return true;
    };

    handlePrevious = async () => {
        if (!await this.canMoveOut()) return;
        this.props.onPrevious();
    };

    handleNext = async () => {
        if (!await this.canMoveOut()) return;
        this.props.onNext();
    };

    handleAfterSave = (closeDialog: boolean, styleChanged: boolean) => {
        if (!closeDialog) return;
        this.props.onOk();
        if (!styleChanged) return;
        window.location.reload();
    };

    render() {
        const props = this.props;
        const state = this.state;
        if (!state.companyEdit) return null;
        const company = state.company;
        const isNew = !company || company.isNew();
        return (
            <Dialog
                id="company-edit-dialog"
                maxWidth="xl"
                maxHeight="830px"
                title={!company ? Translations.Loading + "..." : (isNew ? Translations.AddCompany : (Translations.EditCompany + ": " + company.code))}
                dialogTabs={
                    <DialogTabs
                        tabs={[
                            { id: CompanyEditViewMode.General, title: Translations.BasicInformation },
                            { id: CompanyEditViewMode.NewCompany, title: Translations.NewCompanyInformation, hidden: !isNew },
                            { id: CompanyEditViewMode.Permissions, title: Translations.PermissionsForSections },
                            { id: CompanyEditViewMode.CompanyGroups, title: Translations.CompanyGroups, hidden: !this.getCompanyGroupsEnabled() },
                            { id: CompanyEditViewMode.Partners, title: Translations.Partners, hidden: !this.getPartnerCompaniesEnabled() },
                            { id: CompanyEditViewMode.ExternalSystems, title: Translations.ExternalSystems, hidden: !this.getExternalSystemsExists() },
                            { id: CompanyEditViewMode.IntraModules, title: Translations.ExternalIntraSystems, hidden: !this.getExternalIntraSystemsExists() }
                        ]}
                        activeTabId={state.companyEditViewMode}
                        onChangeTab={this.handleToggleActiveMenuItem}
                    />
                }
                body={
                    <CompanyEditView
                        ref={this.companyEditViewRef}
                        onDialog={true}
                        sessionState={props.sessionState}
                        companyEdit={state.companyEdit}
                        companyId={props.companyId}
                        viewMode={state.companyEditViewMode}
                        onRefreshSessionState={props.onRefreshSessionState}
                        onAfterSave={this.handleAfterSave}
                        onCompanyLoaded={this.handleCompanyLoaded}
                    />
                }
                actions={
                    <Box display="flex" justifyContent="space-between" width="100%">
                        <DialogItemsNav
                            hasPrevious={props.hasPrevious}
                            hasNext={props.hasNext}
                            onPrevious={this.handlePrevious}
                            onNext={this.handleNext}
                            modifyUser={!isNew ? state.company.modifyUser : null}
                        />
                        <DialogButtons
                            isNew={isNew}
                            onCancel={this.handleCancel}
                            onRemove={!isNew ? this.handleRemove : null}
                            onSave={this.handleSave}
                        />
                    </Box>
                }
                onClose={this.handleCancel}
            />
        );
    }
}